// components/VATManagement.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import VATInputForm from "./VATInputForm";
import VATOutputForm from "./VATOutputForm";
import VATRecordsList from "./VATRecordsList";
import Form2306Form from "./Form2306Form";
import Form2307CertificatesPanel from "./Form2307CertificatesPanel";
import form2307API from "../api/form2307API";
import vatAPI from "../api/vatAPI";
import formAPI from "../api/formAPI";
import MoneyInput from "./MoneyInput";
import axiosInstance from "../api/axiosConfig";
import useCompanyContext from "../hooks/useCompanyContext";
import Button from "./Button";
import FilingCard from "./FilingCard";
import notify from "../lib/notify";
import { isVatWithholdingAtc } from "../utils/withholdingAtc";

const MONTH_OPTIONS = [
  { value: 1, label: "Jan" }, { value: 2, label: "Feb" }, { value: 3, label: "Mar" },
  { value: 4, label: "Apr" }, { value: 5, label: "May" }, { value: 6, label: "Jun" },
  { value: 7, label: "Jul" }, { value: 8, label: "Aug" }, { value: 9, label: "Sep" },
  { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Dec" },
];

const QUARTER_MONTHS = {
  1: [{ value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' }],
  2: [{ value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' }],
  3: [{ value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' }],
  4: [{ value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }],
};

const calculateQuarterFromDate = (date) => {
  const month = date.getMonth(); // 0-11
  if (month <= 2) return 1;
  if (month <= 5) return 2;
  if (month <= 8) return 3;
  return 4;
};

const calculateQuarterFromMonth = (month1to12) => {
  const month = Number(month1to12);
  if (!month) return null;
  return Math.floor((month - 1) / 3) + 1;
};

// Withholding of BUSINESS TAX (the RR 4-2021 5% government VAT withholding,
// ATC WV010 goods / WV020 services) is the only 2307 that credits against VAT
// payable. Every other 2307 on a client is expanded withholding tax, which
// credits against INCOME tax — counting those here would understate VAT due.
// The predicate is shared with the SAWT exporter (utils/withholdingAtc.js) so
// the VAT credit and the SAWT file always select the same certificates.
const sumWithholdingVat2307 = (records, matchesPeriod) =>
  (records || [])
    .filter((r) => !r.isVoided && isVatWithholdingAtc(r.atcCode) && matchesPeriod(r))
    .reduce((acc, r) => acc + (Number(r.taxWithheld) || 0), 0);

const getFYKey = (clientId) => (clientId ? `activeFiscalYear:${clientId}` : null);

const resolveFiscalYear = (clientId, selectedYear) => {
  const fromSelected = parseInt(selectedYear, 10);
  if (fromSelected && !isNaN(fromSelected)) return fromSelected;

  const key = getFYKey(clientId);
  const headerFY = key ? parseInt(localStorage.getItem(key), 10) : null;
  return headerFY && !isNaN(headerFY) ? headerFY : null;
};

const VATManagement = ({ client }) => {
  const companyContext = useCompanyContext();
  const tenantType = localStorage.getItem("tenantType") || null;
  const vatSectionKey = client?._id ? `vatManagement:section:${client._id}` : null;

  const [activeSection, setActiveSection] = useState(() => {
    if (!vatSectionKey) return "output";
    return sessionStorage.getItem(vatSectionKey) || "output";
  });

  const handleSetActiveSection = (section) => {
    setActiveSection(section);
    if (vatSectionKey) sessionStorage.setItem(vatSectionKey, section);
  };

  // VAT
  const [vatRecords, setVatRecords] = useState({ input: [], output: [] });

  // 2306
  const [form2306Records, setForm2306Records] = useState([]);

  // 2307. Only the withholding-VAT ones (WV series) credit against VAT payable;
  // the rest are expanded withholding tax and credit against income tax. The
  // full set is fetched so the records list can show them all, and the credit
  // is narrowed where it is computed.
  const [form2307Records, setForm2307Records] = useState([]);

  const [summary, setSummary] = useState(null);

  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState({
    quarter: null,
    year: null,
    month: null,
  });

  // Excess (manual)
  const [excessPrevQuarter, setExcessPrevQuarter] = useState(0);
  const [savingExcess, setSavingExcess] = useState(false);
  const [summaryMonth, setSummaryMonth] = useState(null);
  const [summaryPeriod, setSummaryPeriod] = useState({
    quarter: selectedPeriod.quarter,
    year: selectedPeriod.year,
  });
  const [excessApplyToMonth, setExcessApplyToMonth] = useState(null);

  // Local FY/Quarter for excess tab (independent of selectedPeriod)
  const [excessFY, setExcessFY] = useState(
    selectedPeriod.year || new Date().getFullYear()
  );
  const [excessQuarter, setExcessQuarter] = useState(
    selectedPeriod.quarter || calculateQuarterFromDate(new Date())
  );

  useEffect(() => {
    console.log("[VAT] Company context:", {
      currentCompany: client || null,
      tenantType,
      ...companyContext,
    });
  }, [client, tenantType, companyContext]);

  const fyOptions = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
  const quarterOptions = [
    { value: 1, label: 'Q1 (Jan - Mar)' },
    { value: 2, label: 'Q2 (Apr - Jun)' },
    { value: 3, label: 'Q3 (Jul - Sep)' },
    { value: 4, label: 'Q4 (Oct - Dec)' },
  ];

  // FY header sync
  useEffect(() => {
    const onFYChange = () => {
      const key = client?._id ? `activeFiscalYear:${client._id}` : null;
      const headerFY = key ? parseInt(localStorage.getItem(key), 10) : null;
      if (headerFY && !isNaN(headerFY)) {
        setSelectedPeriod((prev) => ({ ...prev, year: headerFY }));
      }
    };

    window.addEventListener("fiscalYear:changed", onFYChange);
    return () => window.removeEventListener("fiscalYear:changed", onFYChange);
  }, [client?._id]);

  // Sync excess tab selectors when selectedPeriod changes
  useEffect(() => {
    if (selectedPeriod.year) setExcessFY(selectedPeriod.year);
    if (selectedPeriod.quarter) setExcessQuarter(selectedPeriod.quarter);
  }, [selectedPeriod.year, selectedPeriod.quarter]);

  // Reset summaryMonth when selectedPeriod.quarter changes
  useEffect(() => {
    setSummaryMonth(null);
    setSummaryPeriod({
      quarter: selectedPeriod.quarter,
      year: selectedPeriod.year,
    });
  }, [selectedPeriod.quarter, selectedPeriod.year]);

  // load excess per FY/Q from backend
  const fetchExcessFromBackend = useCallback(async () => {
    const fy = excessFY || selectedPeriod.year;
    const q = excessQuarter || selectedPeriod.quarter;

    if (!client?._id || !fy || !q) {
      setExcessPrevQuarter(0);
      return;
    }

    try {
      const response = await vatAPI.getQuarterAdjustment(client._id, {
        quarter: q,
        fiscalYear: fy,
      });
      const data = response?.data?.data ?? response?.data ?? {};
      setExcessPrevQuarter(Number(data?.excessFromPrevQuarter) || 0);
      setExcessApplyToMonth(
        Number(data?.applyToMonth) || (q - 1) * 3 + 1
      );
    } catch (err) {
      console.error('Error fetching excess:', err);
      setExcessPrevQuarter(0);
    }
  }, [client?._id, excessFY, excessQuarter, selectedPeriod.year, selectedPeriod.quarter]);

  useEffect(() => {
    fetchExcessFromBackend();
  }, [fetchExcessFromBackend]);

  const persistExcess = useCallback(async () => {
    const clientId = client?._id;
    const fy = excessFY || resolveFiscalYear(clientId, selectedPeriod.year);
    const q = excessQuarter || selectedPeriod.quarter;

    if (!clientId || !fy || !q) return;

    try {
      setSavingExcess(true);
      const payload = {
        quarter: q,
        fiscalYear: fy,
        excessFromPrevQuarter: Number(excessPrevQuarter) || 0,
        applyToMonth: excessApplyToMonth || null,
        notes: '',
      };

      console.log('[VAT Excess] saveQuarterAdjustment payload:', payload);

      await vatAPI.saveQuarterAdjustment(clientId, {
        ...payload,
      });
      // Refresh to confirm save
      await fetchExcessFromBackend();
    } catch (err) {
      console.error('Error saving excess:', err);
      notify.error("Couldn't save", err.message || 'Unknown error — try again.');
    } finally {
      setSavingExcess(false);
    }
  }, [client?._id, excessFY, excessQuarter, selectedPeriod.year, selectedPeriod.quarter, excessPrevQuarter, excessApplyToMonth, fetchExcessFromBackend]);

  /* =======================
      FETCH VAT
  ======================= */
  const fetchVATRecords = useCallback(async () => {
    if (!client?._id) return;

    try {
      setLoading(true);
      const response = await vatAPI.getByClient(client._id);

      const allRecords = Array.isArray(response?.data)
        ? response.data
        : (response?.data?.records || []);

      setVatRecords({
        input: allRecords.filter((r) => r.recordType === "input"),
        output: allRecords.filter((r) => r.recordType === "output"),
      });
    } catch (err) {
      console.error("Error fetching VAT records", err);
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [client?._id]);

  /* =======================
      FETCH 2306 (Quarter + FY, optional Month)
  ======================= */
  const fetchForm2306Records = useCallback(async () => {
    if (!client?._id) return;

    const clientId = client._id;
    const fy = resolveFiscalYear(clientId, selectedPeriod.year);
    const q = selectedPeriod.quarter;

    try {
      const params = {
        limit: 2000,
        page: 1,
      };

      if (fy) params.fiscalYear = fy;
      if (q) params.quarter = q;

      const res = await formAPI.getForm2306ByClient(clientId, params);
      const records = res?.data?.records || res?.data?.data?.records || [];
      setForm2306Records(Array.isArray(records) ? records : []);
    } catch (err) {
      console.error("Error fetching Form 2306 records", err);
      setForm2306Records([]);
    }
  }, [client?._id, selectedPeriod.year, selectedPeriod.quarter]);

  /* =======================
      FETCH 2307 (Quarter + FY)
      Certificates are generated automatically when a transaction with an ATC is
      posted, so this is purely a read — there is nothing to encode here.
     ======================= */
  const fetchForm2307Records = useCallback(async () => {
    if (!client?._id) return;

    const clientId = client._id;
    const fy = resolveFiscalYear(clientId, selectedPeriod.year);
    const q = selectedPeriod.quarter;
    if (!fy) return;

    try {
      const res = await form2307API.list(clientId, {
        fiscalYear: fy,
        ...(q ? { quarter: q } : {}),
        limit: 2000,
      });
      const records = Array.isArray(res?.data) ? res.data : [];
      setForm2307Records(records);
    } catch (err) {
      console.error("Error fetching Form 2307 records", err);
      setForm2307Records([]);
    }
  }, [client?._id, selectedPeriod.year, selectedPeriod.quarter]);

  useEffect(() => {
    fetchVATRecords();
  }, [fetchVATRecords]);

  // auto select period
  useEffect(() => {
    if (!initialLoadDone) return;
    if (selectedPeriod.quarter) return;

    const now = new Date();
    const clientId = client?._id;
    const headerFY = resolveFiscalYear(clientId, null) || now.getFullYear();

    setSelectedPeriod({
      quarter: calculateQuarterFromDate(now),
      year: headerFY,
      month: now.getMonth() + 1,
    });
  }, [initialLoadDone, selectedPeriod.quarter, client?._id]);

  useEffect(() => {
    fetchForm2306Records();
  }, [fetchForm2306Records]);

  useEffect(() => {
    fetchForm2307Records();
  }, [fetchForm2307Records]);

  /* =======================
      VAT filter
  ======================= */
  const recordMatchesPeriod = useCallback((r) => {
    if (!selectedPeriod.quarter || !selectedPeriod.year) return true;

    const quarterMatch = r.quarter === selectedPeriod.quarter && r.fiscalYear === selectedPeriod.year;
    if (!quarterMatch) return false;

    if (!selectedPeriod.month) return true;
    return r.month === selectedPeriod.month;
  }, [selectedPeriod]);

  /* =======================
      SUMMARY
  ======================= */
  const calculateSummary = useCallback(() => {
    // Filter by summaryPeriod (quarter/year) instead of selectedPeriod
    let inputRecords = vatRecords.input.filter(r =>
      (!summaryPeriod.quarter || (
        r.quarter === summaryPeriod.quarter &&
        r.fiscalYear === summaryPeriod.year
      ))
    );
    let outputRecords = vatRecords.output.filter(r =>
      (!summaryPeriod.quarter || (
        r.quarter === summaryPeriod.quarter &&
        r.fiscalYear === summaryPeriod.year
      ))
    );

    // Filter by summaryMonth if specified
    if (summaryMonth !== null) {
      inputRecords = inputRecords.filter((r) => r.month === summaryMonth);
      outputRecords = outputRecords.filter((r) => r.month === summaryMonth);
    }

    const sum = (arr, field) => arr.reduce((acc, r) => acc + (Number(r[field]) || 0), 0);

    const inputVAT = sum(inputRecords, "vatAmount");
    const outputVAT = sum(outputRecords, "vatAmount");

    // ✅ quarter total of 2306 withheld - filter by month if specified
    const filtered2306 = (form2306Records || []).filter((r) => {
      const recordYear = Number(r.fiscalYear);
      const recordQuarter = Number(r.quarter) || calculateQuarterFromMonth(r.month);

      const periodMatch = !summaryPeriod.quarter || (
        recordQuarter === summaryPeriod.quarter &&
        recordYear === summaryPeriod.year
      );

      const monthMatch = summaryMonth === null || Number(r.month) === summaryMonth;
      return periodMatch && monthMatch;
    });
    const total2306 = filtered2306.reduce((acc, r) => acc + (Number(r.taxWithheld) || 0), 0);

    // Same period test as the 2306 filter above, applied to the 2307s that
    // credit against VAT. Since RR 4-2021 the 5% government VAT withholding is
    // certificated on a 2307 instead of a 2306, so leaving this out would drop
    // the credit for every newly posted transaction.
    const matches2307Period = (r) => {
      const recordYear = Number(r.fiscalYear);
      const recordQuarter = Number(r.quarter) || calculateQuarterFromMonth(r.month);
      const periodMatch = !summaryPeriod.quarter || (
        recordQuarter === summaryPeriod.quarter && recordYear === summaryPeriod.year
      );
      const monthMatch = summaryMonth === null || Number(r.month) === summaryMonth;
      return periodMatch && monthMatch;
    };
    const creditable2307 = (form2307Records || []).filter(
      (r) => !r.isVoided && isVatWithholdingAtc(r.atcCode) && matches2307Period(r)
    );
    const total2307 = creditable2307.reduce((acc, r) => acc + (Number(r.taxWithheld) || 0), 0);

    // Excess only applies when:
    // 1. No month filter (full quarter view) — always apply
    // 2. Month filter active AND selected month matches excessApplyToMonth
    const shouldApplyExcess = !summaryMonth || summaryMonth === excessApplyToMonth;
    const prevExcess = shouldApplyExcess ? Number(excessPrevQuarter) || 0 : 0;
    const vatPayable = outputVAT - inputVAT - total2306 - total2307 - prevExcess;

    return {
      inputVAT,
      outputVAT,
      form2306: total2306,
      form2307: total2307,
      excessPrevQuarter: prevExcess,
      vatPayable,
      inputCount: inputRecords.length,
      outputCount: outputRecords.length,
      form2306Count: filtered2306.length,
      form2307Count: creditable2307.length,
      // SAWT credits claimed on the 2550Q. Same certificates as the 2307 term
      // in vatPayable today, but a separate field on purpose: the payable
      // formula waits on accountant open item 6, and changing it must not
      // silently move the figure the SAWT attachment reconciles against.
      sawtCredits2307: total2307,
    };
  }, [vatRecords, summaryPeriod, form2306Records, form2307Records, excessPrevQuarter, excessApplyToMonth, summaryMonth]);

  useEffect(() => {
    setSummary(calculateSummary());
  }, [calculateSummary]);

  /* =======================
      HANDLERS
  ======================= */
  const reloadAll = useCallback(async () => {
    await fetchVATRecords();
    await fetchForm2306Records();
    await fetchForm2307Records();
  }, [fetchVATRecords, fetchForm2306Records, fetchForm2307Records]);

  const autoPostNewVATRecordToGL = useCallback(async (record) => {
    const recordId = record?._id;
    const accountingClientId = record?.accountingClient || client?._id;

    if (!recordId || !accountingClientId || !client?._id) {
      return false;
    }

    try {
      await axiosInstance.post(`/vat/clients/${client._id}/post-record-to-gl`, {
        recordId,
        accountingClientId,
      });
      return true;
    } catch (error) {
      console.error("Auto-post VAT record to GL failed:", error?.response?.data || error?.message || error);
      return false;
    }
  }, [client?._id]);

  const handleVATRecordAdded = useCallback(async (savedRecord) => {
    const autoPostSucceeded = await autoPostNewVATRecordToGL(savedRecord);

    if (autoPostSucceeded) {
      await reloadAll();
      return;
    }

    await fetchVATRecords();
  }, [autoPostNewVATRecordToGL, reloadAll, fetchVATRecords]);

  const handleVATRecordUpdated = useCallback(async (updatedRecord) => {
    console.log('[VATManagement] handleVATRecordUpdated received:', {
      _id: updatedRecord?._id,
      grossAmount: updatedRecord?.grossAmount,
      vatAmount: updatedRecord?.vatAmount,
      netAmount: updatedRecord?.netAmount,
      isPostedToGL: updatedRecord?.isPostedToGL,
      glJournalEntry: updatedRecord?.glJournalEntry,
    });

    const shouldAdjustGL =
      updatedRecord?.isPostedToGL && updatedRecord?.glJournalEntry && updatedRecord?._id && client?._id;

    if (shouldAdjustGL) {
      try {
        console.log('[VATManagement] adjustGLRecord start', {
          recordId: updatedRecord._id,
          accountingClientId: client._id,
        });
        // step 2: only run after VAT update has resolved and callback is invoked
        await vatAPI.adjustGLRecord(client._id, {
          recordId: updatedRecord._id,
          accountingClientId: client._id,
        });
        console.log('[VATManagement] adjustGLRecord success', { recordId: updatedRecord._id });
      } catch (error) {
        console.error("GL adjustment failed silently:", error);
      }
    }

    // step 3: refresh UI after adjustment attempt completes
    await reloadAll();
  }, [client?._id, reloadAll]);

  const handle2306RecordAdded = useCallback(async (savedRecords = []) => {
    await fetchForm2306Records();

    // The list above is filtered to selectedPeriod's quarter/year. Since the
    // backend now derives each record's real period from its own
    // transactionDate (not the batch's active period), a record saved while
    // viewing e.g. Q3 can correctly belong to Q2 — and would then silently
    // not show up in this still-Q3-filtered list, looking like it never
    // saved. Detect that and either jump the view to match, or say so.
    if (Array.isArray(savedRecords) && savedRecords.length > 0) {
      const periods = new Set(
        savedRecords
          .filter((r) => r?.quarter && r?.fiscalYear)
          .map((r) => `${r.quarter}-${r.fiscalYear}`)
      );

      if (periods.size === 1) {
        const [savedQuarter, savedYear] = Array.from(periods)[0].split('-').map(Number);
        if (savedQuarter !== selectedPeriod.quarter || savedYear !== selectedPeriod.year) {
          setSelectedPeriod((prev) => ({ ...prev, quarter: savedQuarter, year: savedYear, month: null }));
          notify.success('Switched period', `Your record is in Q${savedQuarter} ${savedYear} — switched the view to show it.`);
        }
      } else if (periods.size > 1) {
        notify.success('Saved', 'Records saved across more than one period — switch quarters to see all of them.');
      }
    }
  }, [fetchForm2306Records, selectedPeriod.quarter, selectedPeriod.year]);

  /* =======================
      PERIOD OPTIONS (VAT-based)
  ======================= */
  const uniquePeriods = useMemo(() => {
    const map = new Map();
    [...vatRecords.input, ...vatRecords.output].forEach((r) => {
      if (r.quarter && r.fiscalYear) {
        map.set(`${r.quarter}-${r.fiscalYear}`, {
          quarter: r.quarter,
          year: r.fiscalYear,
          label: `Q${r.quarter} ${r.fiscalYear}`,
        });
      }
    });

    if (map.size === 0 && selectedPeriod.quarter && selectedPeriod.year) {
      map.set(`${selectedPeriod.quarter}-${selectedPeriod.year}`, {
        quarter: selectedPeriod.quarter,
        year: selectedPeriod.year,
        label: `Q${selectedPeriod.quarter} ${selectedPeriod.year}`,
      });
    }

    return Array.from(map.values()).sort((a, b) => b.year - a.year || b.quarter - a.quarter);
  }, [vatRecords.input, vatRecords.output, selectedPeriod.quarter, selectedPeriod.year]);

  // ✅ merged records for list
  const mergedRecordsForList = useMemo(() => {
    // 2307s are tagged explicitly. They share the vendor/baseAmount/taxWithheld
    // shape of a 2306, so VATRecordsList's kind heuristics cannot tell them
    // apart on their own — the tag is what keeps the Form 2307 filter honest.
    const tagged2307 = (form2307Records || []).map((r) => ({ ...r, formType: "2307" }));
    return [...vatRecords.input, ...vatRecords.output, ...(form2306Records || []), ...tagged2307];
  }, [vatRecords.input, vatRecords.output, form2306Records, form2307Records]);

  if (!initialLoadDone && loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-10 w-10 border-b-2 border-primary-500 rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">VAT Management</h2>
          <p className="text-ink-body">Manage VAT for {client?.name}</p>
        </div>
      </div>

      {/* NAV */}
      <div className="flex gap-4 border-b border-ink-line pb-2">
        {["output", "input", "2306", "2307", "excess", "records", "summary"].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => handleSetActiveSection(tab)}
            className={`pb-2 border-b-2 text-sm rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
              activeSection === tab ? "border-primary-500 text-primary-500" : "border-transparent text-ink-body"
            }`}
          >
            {tab === "2306" || tab === "2307" ? `Form ${tab}` : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div className="bg-white border rounded-lg">
        {activeSection === "output" && (
          <VATOutputForm
            client={client}
            selectedQuarter={selectedPeriod.quarter}
            selectedYear={selectedPeriod.year}
            selectedMonth={selectedPeriod.month}
            onRecordAdded={handleVATRecordAdded}
          />
        )}

        {activeSection === "input" && (
          <VATInputForm
            client={client}
            onRecordAdded={handleVATRecordAdded}
            selectedQuarter={selectedPeriod.quarter}
            selectedYear={selectedPeriod.year}
            selectedMonth={selectedPeriod.month}
          />
        )}

        {activeSection === "2306" && (
          <Form2306Form
            client={client}
            selectedQuarter={selectedPeriod.quarter}
            selectedYear={selectedPeriod.year}
            selectedMonth={selectedPeriod.month} 
            onRecordAdded={handle2306RecordAdded}
          />
        )}

        {/*
          Form 2307 is DISPLAY ONLY here. Certificates are derived automatically
          when a transaction with an ATC is posted on the Output or Input tab, so
          this tab shows what posting produced and lets staff download or reprint
          it — there is deliberately no "Generate" action to re-encode from.
          Contrast the 2306 tab above, which is still a manual entry form.
        */}
        {activeSection === "2307" && (
          <Form2307CertificatesPanel client={client} selectedPeriod={selectedPeriod} />
        )}

        {activeSection === "excess" && (
          <div className="p-6 space-y-6">

            {/* FY + Quarter selectors */}
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="text-sm font-medium text-ink-body">Fiscal Year</label>
                <select
                  value={excessFY}
                  onChange={(e) => setExcessFY(parseInt(e.target.value))}
                  className="border rounded px-3 py-2 w-32 text-sm mt-1 block"
                >
                  {fyOptions.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-ink-body">Quarter</label>
                <select
                  value={excessQuarter}
                  onChange={(e) => setExcessQuarter(parseInt(e.target.value))}
                  className="border rounded px-3 py-2 w-48 text-sm mt-1 block"
                >
                  {quarterOptions.map(q => (
                    <option key={q.value} value={q.value}>{q.label}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => fetchExcessFromBackend()}
                className="border rounded px-3 py-2 text-sm"
              >
                Refresh
              </button>
            </div>

            {/* Excess input */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 max-w-lg">
              <h3 className="text-base font-medium text-ink">
                Excess from previous quarter
              </h3>
              <p className="text-sm text-ink-body mt-1">
                Enter the excess tax credit from the previous quarter to be
                deducted from this quarter's VAT payable.
              </p>

              <div className="flex gap-2 items-center mt-3">
                <div className="flex-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-ink-body text-sm">₱</span>
                  </div>
                  <MoneyInput
                    value={excessPrevQuarter}
                    onValueChange={(value) => setExcessPrevQuarter(value)}
                    className="pl-7 w-full border border-ink-line rounded px-3 py-2 text-sm"
                    decimals={2}
                    disabled={!excessFY || !excessQuarter}
                  />
                </div>
                <Button
                  type="button"
                  onClick={persistExcess}
                  disabled={savingExcess || !excessFY || !excessQuarter}
                  loading={savingExcess}
                  variant="primary"
                  size="sm"
                >
                  Save
                </Button>
              </div>

              <div className="text-xs text-ink-body mt-2">
                Saved per FY/Qtr:{' '}
                <span className="font-medium text-ink">FY {excessFY} / Q{excessQuarter}</span>
              </div>

              {/* Apply to month selector */}
              {excessQuarter && (
                <div className="mt-4">
                  <label className="text-xs font-medium text-ink-body">
                    Apply excess to month
                  </label>
                  <select
                    value={excessApplyToMonth || ''}
                    onChange={(e) => setExcessApplyToMonth(parseInt(e.target.value) || null)}
                    className="border border-ink-line rounded px-3 py-1 text-sm mt-1 w-full"
                  >
                    {QUARTER_MONTHS[excessQuarter]?.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-ink-body mt-1">
                    Excess will only be deducted in the selected month's computation.
                    Default is the first month of the quarter.
                  </p>
                </div>
              )}
            </div>

            {/* Summary cards */}
            {(() => {
              const inputRecords = vatRecords.input.filter(r =>
                r.quarter === excessQuarter && r.fiscalYear === excessFY
              );
              const outputRecords = vatRecords.output.filter(r =>
                r.quarter === excessQuarter && r.fiscalYear === excessFY
              );
              const outputVAT = outputRecords.reduce((acc, r) =>
                acc + (Number(r.vatAmount) || 0), 0);
              const inputVAT = inputRecords.reduce((acc, r) =>
                acc + (Number(r.vatAmount) || 0), 0);
              const inSelectedQuarter = (r) => {
                const recordYear = Number(r.fiscalYear);
                const recordQuarter = Number(r.quarter) || calculateQuarterFromMonth(r.month);
                return recordYear === Number(excessFY) && recordQuarter === Number(excessQuarter);
              };
              const total2306 = (form2306Records || [])
                .filter(inSelectedQuarter)
                .reduce((acc, r) => acc + (Number(r.taxWithheld) || 0), 0);
              // Withholding-VAT 2307s only — the RR 4-2021 5% credit that used
              // to arrive on a 2306. Expanded withholding tax is excluded; it
              // credits against income tax, not VAT.
              const total2307 = sumWithholdingVat2307(form2307Records, inSelectedQuarter);
              const prevExcess = Number(excessPrevQuarter) || 0;
              const vatPayableRaw = outputVAT - inputVAT - total2306 - total2307 - prevExcess;
              const carryOver = vatPayableRaw < 0 ? Math.abs(vatPayableRaw) : 0;

              return (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                    <div className="text-xs text-ink-body">Output VAT</div>
                    <div className="text-lg font-medium text-ink tabular-nums">
                      ₱{outputVAT.toFixed(2)}
                    </div>
                  </div>
                  <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                    <div className="text-xs text-ink-body">Input VAT</div>
                    <div className="text-lg font-medium text-ink tabular-nums">
                      ₱{inputVAT.toFixed(2)}
                    </div>
                  </div>
                  <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                    <div className="text-xs text-ink-body">Form 2306</div>
                    <div className="text-lg font-medium text-ink tabular-nums">
                      ₱{total2306.toFixed(2)}
                    </div>
                  </div>
                  <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                    <div className="text-xs text-ink-body">Form 2307</div>
                    <div className="text-lg font-medium text-ink tabular-nums">
                      ₱{total2307.toFixed(2)}
                    </div>
                    <div className="text-xs text-ink-body">Withholding VAT</div>
                  </div>
                  <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                    <div className="text-xs text-ink-body">Excess prev qtr</div>
                    <div className="text-lg font-medium text-ink tabular-nums">
                      ₱{prevExcess.toFixed(2)}
                    </div>
                  </div>
                  <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                    <div className="text-xs text-primary-700">VAT payable</div>
                    <div className="text-lg font-medium text-primary-700 tabular-nums">
                      {vatPayableRaw < 0 ? '-' : ''}₱{Math.abs(vatPayableRaw).toFixed(2)}
                    </div>
                    {carryOver > 0 && (
                      <div className="text-xs text-primary-700/70 mt-1">
                        Carry-over: ₱{carryOver.toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Formula */}
            <div className="text-xs text-ink-body font-mono">
              Formula:{' '}
              VAT Payable = Output VAT − Input VAT − Form 2306 − Form 2307 (withholding VAT) − Excess Prev Qtr
            </div>

          </div>
        )}

        {activeSection === "records" && (
          <VATRecordsList
            records={mergedRecordsForList}
            onRecordDeleted={reloadAll}
            onRecordUpdated={handleVATRecordUpdated}
            showFilters
            showQuarterFilter
            showActions
            client={client}
          />
        )}

        {activeSection === "summary" && summary && (
          <div className="p-6 space-y-4">
            {/* Period & Month Filter (Summary Tab) */}
            <div className="flex items-center gap-3 flex-wrap mb-4">
              {/* Quarter/Period selector */}
              <select
                value={
                  summaryPeriod.quarter && summaryPeriod.year
                    ? `Q${summaryPeriod.quarter} ${summaryPeriod.year}`
                    : 'all'
                }
                onChange={(e) => {
                  if (e.target.value === 'all') {
                    setSummaryPeriod({ quarter: null, year: null });
                    setSummaryMonth(null);
                    return;
                  }
                  const [q, y] = e.target.value.split(' ');
                  setSummaryPeriod({
                    quarter: parseInt(q.replace('Q', ''), 10),
                    year: parseInt(y, 10),
                  });
                  setSummaryMonth(null);
                }}
                className="border rounded px-3 py-1 text-sm"
              >
                <option value="all">All Periods</option>
                {uniquePeriods.map((p) => (
                  <option key={p.label} value={p.label}>{p.label}</option>
                ))}
              </select>

              {/* Month drill-down — only when quarter selected */}
              {summaryPeriod.quarter && (
                <select
                  value={summaryMonth || ''}
                  onChange={(e) => setSummaryMonth(parseInt(e.target.value) || null)}
                  className="border rounded px-3 py-1 text-sm"
                >
                  <option value="">All Months (Full Quarter)</option>
                  {QUARTER_MONTHS[summaryPeriod.quarter]?.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              )}

              {/* Reset button */}
              {(summaryMonth || summaryPeriod.quarter) && (
                <Button
                  type="button"
                  onClick={() => {
                    setSummaryMonth(null);
                    setSummaryPeriod({ quarter: selectedPeriod.quarter, year: selectedPeriod.year });
                  }}
                  variant="ghost"
                  size="sm"
                >
                  Reset
                </Button>
              )}
            </div>

            {/* Summary Label */}
            {(() => {
              const summaryLabel = summaryMonth && summaryPeriod.quarter
                ? `${QUARTER_MONTHS[summaryPeriod.quarter]?.find(m => m.value === summaryMonth)?.label} ${summaryPeriod.year}`
                : summaryPeriod.quarter
                ? `Q${summaryPeriod.quarter} ${summaryPeriod.year}`
                : 'All Periods';
              return (
                <p className="text-sm text-ink-body mb-2">
                  Showing: <span className="font-medium text-ink">{summaryLabel}</span>
                </p>
              );
            })()}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                <div className="text-xs text-ink-body">Output VAT</div>
                <div className="text-lg font-medium text-ink tabular-nums">₱{summary.outputVAT.toFixed(2)}</div>
                <div className="text-xs text-ink-body">{summary.outputCount} records</div>
              </div>

              <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                <div className="text-xs text-ink-body">Input VAT</div>
                <div className="text-lg font-medium text-ink tabular-nums">₱{summary.inputVAT.toFixed(2)}</div>
                <div className="text-xs text-ink-body">{summary.inputCount} records</div>
              </div>

              <div className="rounded-lg border border-ink-line bg-white px-4 py-3">
                <div className="text-xs text-ink-body">Less: 2306 + 2307 + excess</div>
                <div className="text-lg font-medium text-ink tabular-nums">
                  ₱{(Number(summary.form2306) + Number(summary.form2307) + Number(summary.excessPrevQuarter)).toFixed(2)}
                </div>
                <div className="text-xs text-ink-body">
                  2306: {summary.form2306Count} row(s) · 2307: {summary.form2307Count} row(s)
                </div>
              </div>

              <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                <div className="text-xs text-primary-700">VAT payable</div>
                <div className="text-lg font-medium text-primary-700 tabular-nums">₱{summary.vatPayable.toFixed(2)}</div>
                <div className="text-xs text-primary-700/70">Output − Input − Adj</div>
              </div>
            </div>

            <div className="text-xs text-ink-body font-mono">
              Formula: VAT Payable = Output − Input − 2306 − 2307 (withholding VAT) − Excess Prev Qtr
            </div>

            {summaryPeriod.quarter && summaryPeriod.year && (
              <FilingCard
                client={client}
                formCode="2550Q"
                fiscalYear={summaryPeriod.year}
                quarter={summaryPeriod.quarter}
                creditsClaimed={summary.sawtCredits2307}
                hasSlsp
                onGoToRecords={() => handleSetActiveSection("records")}
                onReviewAttachment={() => handleSetActiveSection("2307")}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VATManagement;